import React, { useState, useEffect, useMemo } from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { Col, Row } from "antd";
import { Message as MessageType, messageType } from "../../../../typings/interfaces";
import { Chat } from "../../../../services/api";
import styles from "./chatImageGallery.scss";

interface ChatImageGalleryProps {
  messages: MessageType[];
  isOwnMessage: boolean;
}

export const ChatImageGallery = ({ messages, isOwnMessage }: ChatImageGalleryProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const [imagesSrc, setImagesSrc] = useState([]);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  const imageMessages = messages.filter(msg => msg.type === messageType.Image);

  // Create a stable dependency key for imageMessages
  const imageMessageIds = useMemo(() =>
    imageMessages.map(m => m.messageId).join(','),
    [imageMessages]
  );

  useEffect(() => {
    const loadImageBlobs = async () => {
      setAllImagesLoaded(false);

      try {
        // Load all blob URLs first
        const blobPromises = imageMessages.map(async (message) => {
          const blobUrl = await Chat.getDocumentBlob(message.content);
          return {
            messageId: message.messageId,
            blobUrl,
            fileName: message.fileName
          };
        });

        const blobResults = await Promise.all(blobPromises);

        // Store all blob URLs
        const blobUrlsMap = {};
        const imageSrcArray = [];

        blobResults.forEach(result => {
          blobUrlsMap[result.messageId] = result.blobUrl;
          imageSrcArray.push({
            src: result.blobUrl,
            alt: result.fileName || 'Image',
            messageId: result.messageId
          });
        });

        setImageBlobUrls(blobUrlsMap);
        setImagesSrc(imageSrcArray);

        // Now wait for all images to actually load
        const imageLoadPromises = imageSrcArray.map((imageSrc) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(imageSrc.messageId);
            img.onerror = () => {
              console.error('Failed to load image:', imageSrc.src);
              resolve(imageSrc.messageId); // Resolve anyway to not block the gallery
            };
            img.src = imageSrc.src;
          });
        });

        await Promise.all(imageLoadPromises);
        setAllImagesLoaded(true);
      } catch (error) {
        console.error('Failed to load image blobs:', error);
        setAllImagesLoaded(true); // Show gallery anyway on error
      }
    };

    if (imageMessages.length > 0) {
      loadImageBlobs();
    } else {
      setAllImagesLoaded(true);
    }

    // Cleanup blob URLs on unmount
    return () => {
      Object.values(imageBlobUrls).forEach(url => {
        if (typeof url === 'string') {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imageMessageIds]);

  const onClick = (e, index) => {
    e.preventDefault();
    if (allImagesLoaded) {
      setIsOpen(true);
      setPhotoIndex(index);
    }
  };

  const renderImage = (imageSrc, className, index) => {
    // Guard against undefined imageSrc
    if (!imageSrc || !imageSrc.src) {
      return (
        <div className={`${className} ${styles.imageWrapper}`}>
          <div className={styles.imagePlaceholder}></div>
        </div>
      );
    }

    return (
      <div className={`${className} ${styles.imageWrapper}`}>
        <div
          className={styles.imageBackground}
          style={{
            backgroundImage: `url(${imageSrc.src})`
          }}
          alt={imageSrc.alt}
        />
      </div>
    );
  };

  if (imageMessages.length === 0) {
    return null;
  }

  // Show loading state while images are loading or if imagesSrc is not populated yet
  if (!allImagesLoaded || imagesSrc.length === 0) {
    return (
      <div className={`${styles.chatImageGallery} ${isOwnMessage ? styles.ownGallery : styles.otherGallery}`}>
        <Row className={styles.carouselWrapper}>
          <Col xs={24}>
            {imageMessages.length === 1 ? (
              <div className={`${styles.imageWrapper} ${styles.carouselImage} ${styles.defaultImage}`}>
                <div className={styles.imagePlaceholder}></div>
              </div>
            ) : (
              imageMessages.slice(0, 4).map((message, index) => (
                <Col
                  key={message.messageId}
                  xs={index === 0 ? 24 : 8}
                  md={index === 0 ? 18 : 6}
                  className={
                    index === 0 ? styles.largeImage : styles.smallImage
                  }
                >
                  <div className={`${styles.imageWrapper} ${styles.carouselImage}`}>
                    <div className={styles.imagePlaceholder}></div>
                    {index === 3 && imageMessages.length - 4 > 0 && (
                      <span className={styles.imageCounter}>
                        <span>+{imageMessages.length - 4}</span>
                      </span>
                    )}
                  </div>
                </Col>
              ))
            )}
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className={`${styles.chatImageGallery} ${isOwnMessage ? styles.ownGallery : styles.otherGallery}`}>
        <Row className={styles.carouselWrapper}>
          <Col xs={24}>
            {imageMessages.length === 1 ? (
              <React.Fragment>
                <span
                  onClick={e => onClick(e, 0)}
                  role="Button"
                  tabIndex={0}
                  className={styles.imageLink}
                >
                  {renderImage(
                    imagesSrc[0],
                    `${styles.carouselImage} ${styles.defaultImage}`,
                    0
                  )}
                </span>
              </React.Fragment>
            ) : (
              imageMessages.slice(0, 4).map((message, index) => (
                <Col
                  key={message.messageId}
                  xs={index === 0 ? 24 : 8}
                  md={index === 0 ? 18 : 6}
                  className={
                    index === 0 ? styles.largeImage : styles.smallImage
                  }
                >
                  <span
                    onClick={e => onClick(e, index)}
                    role="Button"
                    tabIndex={0}
                    className={styles.imageLink}
                  >
                    {renderImage(
                      imagesSrc[index],
                      styles.carouselImage,
                      index
                    )}
                    {index !== 3 || imageMessages.length - 4 <= 0 ? null : (
                      <span className={styles.imageCounter}>
                        <span>+{imageMessages.length - 4}</span>
                      </span>
                    )}
                  </span>
                </Col>
              ))
            )}
          </Col>
        </Row>
      </div>

      {isOpen && (
        <React.Fragment>
          <Lightbox
            animationOnKeyInput
            imageCaption={`${photoIndex + 1} / ${imagesSrc.length}`}
            mainSrc={imagesSrc[photoIndex].src}
            nextSrc={imagesSrc[(photoIndex + 1) % imagesSrc.length].src}
            prevSrc={imagesSrc[(photoIndex + imagesSrc.length - 1) % imagesSrc.length].src}
            onCloseRequest={() => setIsOpen(false)}
            onMovePrevRequest={() =>
              setPhotoIndex((photoIndex + imagesSrc.length - 1) % imagesSrc.length)
            }
            onMoveNextRequest={() =>
              setPhotoIndex((photoIndex + 1) % imagesSrc.length)
            }
            wrapperClassName={styles.lightbox}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};


// WEBPACK FOOTER //
// ./src/containers/ChatsPage/SingleChat/Message/ChatImageGallery.tsx